import { useEffect, useState, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import apiClient from "../lib/api";
import PickCard from "../components/PickCard";
import { Link } from "react-router-dom";
import {
  Trophy, Crown,
  Sparkle, ArrowUpRight, Warning, Bookmark, TrendUp,
  TrendDown, X, Star, ChatText, CheckCircle, DiamondsFour, ShieldCheck,
  Fire, Percent, Clock,
} from "@phosphor-icons/react";
import SportIcon from "../components/SportIcon";
import TeamLogo from "../components/betiq/TeamLogo";
import { resolveTeamLogo } from "../lib/resolveTeamLogo";
import {
  ResponsiveContainer, AreaChart, Area, Tooltip, XAxis, YAxis, CartesianGrid,
} from "recharts";
import { toast } from "sonner";
import PushSubscribeCard from "../components/PushSubscribeCard";
import AdminSection from "../components/admin/AdminSection";
import WelcomeTour from "../components/WelcomeTour";
import { BRAND } from "../lib/brand";

// ── Trial Countdown Banner ────────────────────────────────────────────────────
function TrialCountdown({ expiresAt }) {
  const calcRemaining = () => {
    const diff = new Date(expiresAt).getTime() - Date.now();
    if (diff <= 0) return null;
    const h = Math.floor(diff / 3600000);
    const m = Math.floor((diff % 3600000) / 60000);
    const s = Math.floor((diff % 60000) / 1000);
    return { h, m, s, total: diff };
  };

  const [remaining, setRemaining] = useState(calcRemaining());
  const [canceling, setCanceling] = useState(false);

  useEffect(() => {
    const id = setInterval(() => setRemaining(calcRemaining()), 1000);
    return () => clearInterval(id);
  }, [expiresAt]); // eslint-disable-line

  const handleCancel = async () => {
    if (!window.confirm("¿Seguro que quieres cancelar? Perderás el acceso al terminar las 24 horas.")) return;
    setCanceling(true);
    try {
      await apiClient.post("/subscription/cancel");
      toast.success("Suscripción cancelada. Tu acceso continúa hasta que terminen las 24 horas.");
    } catch (e) {
      toast.error("No se pudo cancelar. Intenta desde tu perfil.");
    } finally { setCanceling(false); }
  };

  if (!remaining) return null;

  const urgency = remaining.total < 3 * 3600000; // less than 3h left

  return (
    <div data-testid="trial-countdown-banner"
      className="mb-6 rounded-2xl overflow-hidden"
      style={{
        background: urgency
          ? "linear-gradient(135deg, rgba(239,68,68,0.08), rgba(239,68,68,0.03))"
          : "linear-gradient(135deg, rgba(212,175,55,0.08), rgba(212,175,55,0.02))",
        border: `1px solid ${urgency ? "rgba(239,68,68,0.25)" : "rgba(212,175,55,0.2)"}`,
      }}>
      {/* Franja superior */}
      <div style={{ height: 2, background: urgency
        ? "linear-gradient(90deg, transparent, #ef4444, transparent)"
        : "linear-gradient(90deg, transparent, #D4AF37, transparent)" }} />

      <div className="px-5 py-4">
        <div className="flex flex-wrap items-center gap-4">
          {/* Reloj digital */}
          <div className="flex items-center gap-3 flex-shrink-0">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ background: urgency ? "rgba(239,68,68,0.15)" : "rgba(212,175,55,0.12)", border: urgency ? "1px solid rgba(239,68,68,0.3)" : "1px solid rgba(212,175,55,0.25)" }}>
              <Clock weight="fill" className="w-5 h-5" style={{ color: urgency ? "#ef4444" : "#D4AF37" }} />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-widest font-bold mb-0.5" style={{ color: urgency ? "#ef4444" : "#D4AF37" }}>
                {urgency ? "⚠ Prueba terminando" : "Tu prueba gratis"}
              </p>
              <div className="flex items-baseline gap-1">
                <span className="font-black tabular-nums text-2xl text-white leading-none">
                  {String(remaining.h).padStart(2, "0")}
                  <span className="text-zinc-600 text-lg mx-0.5">:</span>
                  {String(remaining.m).padStart(2, "0")}
                  <span className="text-zinc-600 text-lg mx-0.5">:</span>
                  {String(remaining.s).padStart(2, "0")}
                </span>
                <span className="text-zinc-500 text-xs">restantes</span>
              </div>
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <p className="text-sm text-zinc-300">
              {urgency
                ? "Tu trial termina pronto. Si no cancelas, tu suscripción de $50/semana se activará automáticamente."
                : "Tienes acceso completo. Si no cancelas antes de que termine el contador, se activará tu suscripción de $50/semana."}
            </p>
          </div>

          {/* Botón cancelar */}
          <button onClick={handleCancel} disabled={canceling}
            data-testid="trial-cancel-btn"
            className="flex-shrink-0 flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold transition disabled:opacity-50"
            style={{ background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.25)", color: "#ef4444" }}>
            {canceling
              ? <span className="w-3.5 h-3.5 border border-red-400 border-t-transparent rounded-full animate-spin" />
              : <X className="w-3.5 h-3.5" />}
            Cancelar suscripción
          </button>
        </div>

        {/* Barra de progreso */}
        <div className="mt-3 h-1 rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.05)" }}>
          <div className="h-full rounded-full transition-all duration-1000"
            style={{
              width: `${Math.min(100, (1 - remaining.total / 86400000) * 100)}%`,
              background: urgency ? "#ef4444" : "#D4AF37",
            }} />
        </div>
        <div className="flex justify-between mt-1">
          <span className="text-[10px] text-zinc-600">Inicio</span>
          <span className="text-[10px] text-zinc-600">24 horas — se cobra $50</span>
        </div>
      </div>
    </div>
  );
}


/* ── Récord de Ronald (fetches /api/public/winrate) ─────────── */
function RonaldRecord() {
  const [data, setData] = useState(null);
  useEffect(() => {
    apiClient.get("/public/winrate").then(r => setData(r.data)).catch(() => {});
  }, []);
  if (!data || data.settled === 0) return null;

  return (
    <div
      data-testid="ronald-record-bar"
      className="mb-6 rounded-2xl overflow-hidden relative"
      style={{
        background: "linear-gradient(135deg, #141418 0%, #1a1a20 100%)",
        border: "1px solid rgba(212,175,55,0.2)",
        boxShadow: "0 4px 24px rgba(0,0,0,0.4)",
      }}
    >
      {/* Top gold line */}
      <div style={{ height: 2, background: "linear-gradient(90deg, transparent, #D4AF37, transparent)" }} />

      <div className="px-5 py-4 flex flex-wrap items-center gap-4 lg:gap-8">
        <div className="flex items-center gap-2 flex-shrink-0">
          <ShieldCheck weight="fill" className="w-5 h-5 flex-shrink-0" style={{ color: "#D4AF37" }} />
          <div>
            <div className="text-[9px] uppercase tracking-[0.25em] font-black" style={{ color: "#D4AF37" }}>
              Récord de Ronald
            </div>
            <div className="text-[10px] text-zinc-500">historial completo</div>
          </div>
        </div>

        <div className="flex items-center gap-1.5 flex-shrink-0">
          <CheckCircle weight="fill" className="w-4 h-4 text-emerald-400" />
          <span className="font-black text-xl text-emerald-400" data-testid="record-won">{data.won}</span>
          <span className="text-xs text-zinc-500 font-semibold">Ganadas</span>
        </div>

        <div className="flex items-center gap-1.5 flex-shrink-0">
          <X weight="bold" className="w-4 h-4 text-red-400" />
          <span className="font-black text-xl text-red-400" data-testid="record-lost">{data.lost}</span>
          <span className="text-xs text-zinc-500 font-semibold">Perdidas</span>
        </div>

        {/* Win rate pill */}
        <div className="flex items-center gap-2 px-4 py-2 rounded-full flex-shrink-0"
          style={{ background: "rgba(212,175,55,0.12)", border: "1px solid rgba(212,175,55,0.3)" }}>
          <Percent weight="bold" className="w-3.5 h-3.5" style={{ color: "#D4AF37" }} />
          <span className="font-black text-lg" style={{ color: "#D4AF37" }} data-testid="record-winrate">
            {data.win_rate}%
          </span>
          <span className="text-[10px] text-zinc-400 font-semibold uppercase tracking-wider">Efectividad</span>
        </div>

        {/* Streak */}
        {data.streak > 0 && data.streak_type === "won" && (
          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full flex-shrink-0"
            style={{ background: "rgba(249,115,22,0.1)", border: "1px solid rgba(249,115,22,0.25)" }}>
            <Fire weight="fill" className="w-3.5 h-3.5 text-orange-400" />
            <span className="text-xs font-black text-orange-400">{data.streak} en racha</span>
          </div>
        )}

        {/* Progress bar */}
        <div className="flex-1 min-w-[120px] hidden lg:block">
          <div className="flex justify-between text-[9px] text-zinc-600 mb-1">
            <span>{data.won}G</span>
            <span>{data.lost}P</span>
          </div>
          <div className="h-1.5 rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.06)" }}>
            <div
              className="h-full rounded-full transition-all duration-700"
              style={{
                width: `${data.win_rate}%`,
                background: "linear-gradient(90deg, #10B981, #D4AF37)",
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}


export default function Dashboard() {
  const { user, refresh: refreshAuth } = useAuth();
  const { theme } = useTheme();
  const isLight = theme === "light";
  const [stats, setStats] = useState(null);
  const [picks, setPicks] = useState([]);
  const [events, setEvents] = useState({ nba: [], mlb: [], football: [] });
  const [loading, setLoading] = useState(true);
  const [followedData, setFollowedData] = useState({ picks: [], pnl: {} });
  const [renewalBanner, setRenewalBanner] = useState(false);

  // Detect renewal notification from backend
  useEffect(() => {
    if (user?.renewal_notification_pending) {
      setRenewalBanner(true);
      apiClient.post("/me/ack-renewal").catch(() => {}).finally(() => refreshAuth());
    }
  }, [user?.renewal_notification_pending]); // eslint-disable-line
  const [myReview, setMyReview] = useState(undefined); // undefined=loading, null=none
  const [reviewForm, setReviewForm] = useState({ rating: 5, text: "" });
  const [reviewSending, setReviewSending] = useState(false);
  const [tab, setTab] = useState("hoy"); // "hoy" | "envivo" | "resultados"
  const [showPush, setShowPush] = useState(() => {
    try { return localStorage.getItem("gcr_push_dismissed") !== "1"; } catch { return true; }
  });
  // Tour de bienvenida: solo en el primer login
  const [showTour, setShowTour] = useState(() => {
    try { return localStorage.getItem("gcr_welcome_seen") !== "1"; } catch { return true; }
  });

  const isAdmin = user?.role === "admin";

  const canReview = isAdmin || ((user?.plan === "premium" || user?.plan === "vip") &&
    (user?.subscription_status === "active" || user?.subscription_status === "trialing"));

  const loadReview = useCallback(async () => {
    if (!canReview) return;
    try {
      const r = await apiClient.get("/reviews/my");
      setMyReview(r.data.review || null);
    } catch { setMyReview(null); }
  }, [canReview]);

  useEffect(() => { loadReview(); }, [loadReview]);

  useEffect(() => {
    (async () => {
      try {
        const [s, p, e, f] = await Promise.all([
          apiClient.get("/stats/overview"),
          apiClient.get("/picks", { params: { limit: 6 } }),
          apiClient.get("/events/upcoming", { params: { limit: 3 } }),
          apiClient.get("/user/followed").catch(() => ({ data: { picks: [], pnl: {} } })),
        ]);
        setStats(s.data);
        setPicks(p.data.picks);
        setEvents(e.data);
        setFollowedData(f.data);
      } catch (e) {
        // ignore
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const overall = stats?.overall || {};
  const evolution = stats?.evolution || [];
  const pendingPicks = picks.filter((p) => {
    if (p.status !== "pending") return false;
    // Don't show picks whose match already happened more than 12h ago
    if (p.match_date) {
      const matchTime = new Date(p.match_date).getTime();
      const cutoff = Date.now() - 12 * 3600 * 1000;
      if (matchTime < cutoff) return false;
    } else if (p.created_at) {
      // No match_date: hide if pick was created more than 36h ago (stale)
      const createdTime = new Date(p.created_at).getTime();
      const cutoff = Date.now() - 36 * 3600 * 1000;
      if (createdTime < cutoff) return false;
    }
    return true;
  });
  const recentSettled = picks.filter((p) => p.status !== "pending").slice(0, 3);
  const hasHistory = evolution.length > 0;
  const hasAnyPicks = picks.length > 0;

  // Flatten events for display
  const allEvents = [
    ...(events.football || []).map(e => ({ ...e, sport: "football" })),
    ...(events.nba || []).map(e => ({ ...e, sport: "nba" })),
    ...(events.mlb || []).map(e => ({ ...e, sport: "mlb" })),
  ].slice(0, 6);
  const liveEvents = allEvents.filter(e => e.status === "in");

  const pnl = followedData.pnl || {};
  const profitPositive = (pnl.profit_units || 0) >= 0;

  return (
    <div className="max-w-7xl mx-auto px-4 lg:px-8 pt-8 pb-16">

      {/* Tour de bienvenida (solo primera vez) */}
      <WelcomeTour open={showTour} onClose={() => setShowTour(false)} />

      {/* Renewal celebration banner */}
      {renewalBanner && (
        <div data-testid="renewal-banner"
          className="mb-6 p-4 rounded-xl border flex items-center justify-between gap-3"
          style={{ background: "linear-gradient(135deg, rgba(16,185,129,0.1), rgba(251,191,36,0.05))", borderColor: "rgba(16,185,129,0.25)" }}>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-emerald-500/15 border border-emerald-500/25 flex items-center justify-center flex-shrink-0">
              <Trophy weight="fill" className="w-4.5 h-4.5 text-emerald-400" />
            </div>
            <div>
              <div className="font-bold text-emerald-400">¡Suscripción activada con éxito!</div>
              <div className="text-sm text-zinc-400 mt-0.5">
                Ya tienes acceso completo hasta{" "}
                <span className="text-emerald-300 font-semibold">
                  {user?.subscription_expires_at
                    ? new Date(user.subscription_expires_at).toLocaleDateString("es-ES", { day: "2-digit", month: "long", year: "numeric" })
                    : "tu próxima renovación"}
                </span>.
                Gracias por confiar en {BRAND.name}.
              </div>
            </div>
          </div>
          <button onClick={() => setRenewalBanner(false)} className="text-zinc-600 hover:text-zinc-400 flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Suspension banner — solo para no-admins */}
      {user?.is_suspended && !isAdmin && (
        <div data-testid="suspension-banner" className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-xl flex items-start gap-3">
          <Warning weight="fill" className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
          <div>
            <div className="font-bold text-red-400">Tu cuenta ha sido suspendida</div>
            <div className="text-sm text-zinc-400 mt-0.5">
              El acceso premium está bloqueado. Contacta soporte o renueva tu suscripción para reactivarla.
            </div>
            <Link to="/plans" className="text-sm font-semibold text-red-400 hover:text-red-300 mt-2 inline-block">
              Renovar suscripción →
            </Link>
          </div>
        </div>
      )}


      {/* Trial Countdown — visible solo cuando status === trialing */}
      {user?.subscription_status === "trialing" && user?.subscription_expires_at && !isAdmin && (
        <TrialCountdown expiresAt={user.subscription_expires_at} />
      )}

      {/* ── Tabs: Hoy / En vivo / Resultados ─────────────────────── */}
      <div className="flex gap-2 mb-6" role="tablist" aria-label="Secciones del panel">
        {[
          { id: "hoy", label: "Hoy" },
          { id: "envivo", label: "En vivo", live: liveEvents.length > 0 },
          { id: "resultados", label: "Resultados" },
        ].map((t) => (
          <button
            key={t.id}
            role="tab"
            aria-selected={tab === t.id}
            onClick={() => setTab(t.id)}
            className={`flex-1 sm:flex-none sm:px-8 py-2.5 rounded-xl text-sm font-bold transition-all flex items-center justify-center gap-2 ${
              tab === t.id ? "text-black shadow-lg" : "text-zinc-400 hover:text-zinc-200"
            }`}
            style={
              tab === t.id
                ? { background: "linear-gradient(135deg, var(--gold), var(--gold-light))" }
                : { background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }
            }
          >
            {t.label}
            {t.live && (
              <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" aria-label="Hay partidos en vivo" />
            )}
          </button>
        ))}
      </div>

      {/* ═══════════ TAB: HOY ═══════════ */}
      {tab === "hoy" && (
        <>
          {/* Header compacto */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
            <div className="min-w-0">
              <h1 className="font-black text-2xl lg:text-3xl tracking-tight truncate">
                Hola, {user?.name?.split(" ")[0]}.
              </h1>
              <p className="text-zinc-500 text-sm mt-1">
                {new Date().toLocaleDateString("es-ES", { weekday: "long", day: "numeric", month: "long" })}
                {" · "}{pendingPicks.length} jugada{pendingPicks.length === 1 ? "" : "s"} hoy
              </p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <span
                className="text-[10px] font-black uppercase tracking-[0.15em] px-3 py-1.5 rounded-full"
                style={{
                  background: user?.plan === "vip" || user?.plan === "premium" ? "rgba(212,175,55,0.12)" : "rgba(255,255,255,0.06)",
                  border: "1px solid rgba(212,175,55,0.3)",
                  color: "var(--gold)",
                }}
              >
                {user?.role === "admin" ? "Admin" : user?.plan || "Gratis"}
              </span>
              {user?.plan === "free" && (
                <Link to="/plans" className="btn-primary text-sm" data-testid="dashboard-upgrade-cta">
                  <Crown weight="fill" className="w-4 h-4" /> Premium
                </Link>
              )}
            </div>
          </div>

          {/* IQ DEL DÍA — pick marcado por el admin, o el de mayor IQ como respaldo */}
          {pendingPicks.length > 0 && (() => {
            const iqPick = pendingPicks.find((pk) => pk.is_iq_pick) || pendingPicks[0];
            return (
              <div className="mb-8" data-testid="dashboard-iq-del-dia">
                <div className="text-[11px] tracking-[0.2em] uppercase font-bold mb-3 flex items-center gap-2" style={{ color: "var(--gold)" }}>
                  <DiamondsFour weight="fill" className="w-3.5 h-3.5" /> IQ del Día
                </div>
                <PickCard pick={iqPick} />
              </div>
            );
          })()}

          {/* ── Récord de Ronald Banner ─────────────────────────── */}
          <RonaldRecord />

          {/* Stats compactos: Ganancia / Efectividad / Racha */}
          <div className="grid grid-cols-3 gap-3 mb-8">
            <div className="rounded-2xl p-4 text-center" style={{ background: "#1C1C22", border: "1px solid rgba(255,255,255,0.08)" }}>
              <div className={`font-black text-xl number-ticker ${overall.total_profit >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                {overall.total_profit >= 0 ? "+" : ""}{overall.total_profit?.toFixed(1) || "0.0"}u
              </div>
              <div className="text-[10px] text-zinc-500 uppercase tracking-wider mt-1 font-semibold">Ganancia</div>
            </div>
            <div className="rounded-2xl p-4 text-center" style={{ background: "#1C1C22", border: "1px solid rgba(255,255,255,0.08)" }}>
              <div className="font-black text-xl number-ticker text-white">{overall.win_rate?.toFixed(0) || "0"}%</div>
              <div className="text-[10px] text-zinc-500 uppercase tracking-wider mt-1 font-semibold">Efectividad</div>
            </div>
            <div className="rounded-2xl p-4 text-center" style={{ background: "#1C1C22", border: "1px solid rgba(255,255,255,0.08)" }}>
              <div className="font-black text-xl number-ticker" style={{ color: "var(--gold)" }}>
                {overall.won || 0}-{overall.lost || 0}
              </div>
              <div className="text-[10px] text-zinc-500 uppercase tracking-wider mt-1 font-semibold">Racha G-P</div>
            </div>
          </div>

          {/* Jugadas del día */}
          <AdminSection icon={Sparkle} title="Jugadas del día" subtitle="Señales de hoy" color="amber" className="mb-8"
            action={<Link to="/jugadas?status=pending" className="text-xs font-semibold flex items-center gap-1 flex-shrink-0" style={{ color: "var(--gold)" }} data-testid="dashboard-view-all-pending">
              Ver todas <ArrowUpRight weight="bold" className="w-3.5 h-3.5" />
            </Link>}>
            {loading ? (
              <div className="grid md:grid-cols-3 gap-4">
                {[1, 2, 3].map((i) => <div key={i} className="surface rounded-xl h-64 animate-pulse" />)}
              </div>
            ) : pendingPicks.length === 0 ? (
              <EmptyCard
                title={hasAnyPicks ? "No hay jugadas pendientes ahora mismo" : "Aún no hay jugadas publicadas"}
                desc="Las próximas jugadas aparecerán aquí tan pronto se registren."
              />
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {pendingPicks.slice(0, 6).map((p) => <PickCard key={p.id} pick={p} />)}
              </div>
            )}
          </AdminSection>

          {/* Mis jugadas seguidas */}
          {followedData.picks?.length > 0 && (
            <AdminSection icon={Bookmark} title="Jugadas que seguí" subtitle="Mis seguimientos" color="violet" className="mb-8" testid="followed-picks-section">
              <div className="mb-5 rounded-2xl p-4 flex items-center gap-4"
                style={{ background: "#16161A", border: "1px solid rgba(255,255,255,0.07)" }}>
                {profitPositive
                  ? <TrendUp weight="bold" className="w-6 h-6 text-emerald-400 flex-shrink-0" />
                  : <TrendDown weight="bold" className="w-6 h-6 text-red-400 flex-shrink-0" />}
                <div>
                  <div className={`text-3xl font-black number-ticker ${profitPositive ? "text-emerald-400" : "text-red-400"}`}>
                    {profitPositive ? "+" : ""}{pnl.profit_units?.toFixed(2) || "0.00"} u
                  </div>
                  <div className="text-xs text-zinc-500">
                    {pnl.won || 0} ganadas · {pnl.lost || 0} perdidas · {pnl.pending || 0} pendientes
                  </div>
                </div>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {followedData.picks.slice(0, 6).map((p) => <PickCard key={p.id} pick={p} compact />)}
              </div>
            </AdminSection>
          )}

          {/* ── Push Notifications (dismissable, al final) ──────── */}
          {showPush && (
            <div className="mb-8 relative" data-testid="dashboard-push-card">
              <button
                onClick={() => { try { localStorage.setItem("gcr_push_dismissed", "1"); } catch {} setShowPush(false); }}
                aria-label="Ocultar"
                className="absolute top-2 right-2 z-10 w-7 h-7 rounded-full flex items-center justify-center text-zinc-500 hover:text-zinc-300 hover:bg-white/10 transition"
              >
                <X className="w-4 h-4" />
              </button>
              <PushSubscribeCard />
            </div>
          )}

          {/* Review section — premium only */}
          {canReview && (
            <AdminSection icon={Star} title="Dejar reseña" subtitle="Tu opinión" color="sky" className="mb-8" testid="review-section">

              {myReview === undefined ? null : myReview ? (
                <div className="surface rounded-xl p-6 flex items-start gap-4" data-testid="review-submitted">
                  <CheckCircle weight="fill" className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-bold text-sm">
                      {myReview.status === "approved" ? "Tu reseña está publicada" : "Reseña enviada — pendiente de aprobación"}
                    </div>
                    <div className="text-xs text-zinc-500 mt-1">{myReview.text}</div>
                    <div className="flex gap-0.5 mt-2">
                      {[1,2,3,4,5].map(n => (
                        <Star key={n} weight={n <= myReview.rating ? "fill" : "regular"}
                          className={`w-3.5 h-3.5 ${n <= myReview.rating ? "text-yellow-400" : "text-zinc-700"}`} />
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="surface rounded-xl p-6">
                  <div className="flex gap-1 mb-4">
                    {[1,2,3,4,5].map(n => (
                      <button key={n} data-testid={`review-star-${n}`}
                        onClick={() => setReviewForm(f => ({ ...f, rating: n }))}
                        className="p-1 transition-transform hover:scale-110">
                        <Star weight={n <= reviewForm.rating ? "fill" : "regular"}
                          className={`w-6 h-6 ${n <= reviewForm.rating ? "text-yellow-400" : "text-zinc-600"}`} />
                      </button>
                    ))}
                  </div>
                  <textarea
                    data-testid="review-text-input"
                    className="input-field w-full h-24 resize-none mb-4"
                    placeholder={`Comparte tu experiencia con ${BRAND.name}...`}

                    value={reviewForm.text}
                    onChange={e => setReviewForm(f => ({ ...f, text: e.target.value }))}
                  />
                  <button
                    data-testid="review-submit-btn"
                    onClick={async () => {
                      if (reviewForm.text.trim().length < 10) return toast.error("Mínimo 10 caracteres");
                      setReviewSending(true);
                      try {
                        await apiClient.post("/reviews", reviewForm);
                        toast.success("¡Reseña enviada! Será revisada por el equipo.");
                        await loadReview();
                      } catch (err) {
                        toast.error(err.response?.data?.detail || "Error al enviar reseña");
                      }
                      setReviewSending(false);
                    }}
                    disabled={reviewSending}
                    className="btn-primary text-sm"
                  >
                    {reviewSending ? "Enviando..." : <><ChatText weight="bold" className="w-4 h-4" /> Enviar reseña</>}
                  </button>
                </div>
              )}
            </AdminSection>
          )}
        </>
      )}

      {/* ═══════════ TAB: EN VIVO ═══════════ */}
      {tab === "envivo" && (
        <AdminSection icon={Fire} title="Partidos en vivo" subtitle="En juego ahora" color="rose"
          action={<span className="w-2 h-2 rounded-full bg-red-500 animate-pulse flex-shrink-0" />}>
          {liveEvents.length === 0 ? (
            <EmptyCard
              title="No hay partidos en vivo ahora mismo"
              desc="Cuando empiece un partido de tus jugadas, aparecerá aquí con el marcador en tiempo real."
            />
          ) : (
            <div className="flex gap-4 overflow-x-auto pb-4 -mx-4 px-4 lg:mx-0 lg:px-0 lg:grid lg:grid-cols-2 xl:grid-cols-3 lg:overflow-visible">
              {liveEvents.map((ev) => <LiveEventCard key={`${ev.sport}-${ev.id}`} event={ev} />)}
            </div>
          )}
        </AdminSection>
      )}

      {/* ═══════════ TAB: RESULTADOS ═══════════ */}
      {tab === "resultados" && (
        <>
          {/* Resultados recientes */}
          {recentSettled.length > 0 && (
            <AdminSection icon={Trophy} title="Resultados recientes" subtitle="Historial" color="emerald" className="mb-8">
              <div className="grid md:grid-cols-3 gap-4">
                {recentSettled.map((p) => <PickCard key={p.id} pick={p} compact />)}
              </div>
            </AdminSection>
          )}

          {/* Gráfico + desglose + mi acceso */}
          <div className="grid lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 rounded-2xl p-6 relative overflow-hidden"
              style={{ background: "linear-gradient(145deg, #1C1C22 0%, #14141A 100%)", border: "1px solid rgba(212,175,55,0.15)", boxShadow: "0 8px 32px rgba(0,0,0,0.5)" }}>
              <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 2, background: "linear-gradient(90deg, transparent, rgba(212,175,55,0.6), transparent)" }} />
              <div className="flex items-start justify-between mb-6">
                <div>
                  <div className="text-[10px] text-zinc-500 uppercase tracking-[0.2em] font-bold mb-1">Evolución</div>
                  <div className="font-bold text-2xl tracking-tight">Profit acumulado</div>
                </div>
                {hasHistory && (
                  <div className="flex items-center gap-2 text-xs">
                    <div className="w-2 h-2 rounded-full bg-emerald-400" />
                    <span className="text-zinc-400">Unidades</span>
                  </div>
                )}
              </div>
              <div className="h-[260px]" data-testid="dashboard-evolution-chart">
                {hasHistory ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={evolution}>
                      <defs>
                        <linearGradient id="profitGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#10B981" stopOpacity={0.4} />
                          <stop offset="100%" stopColor="#10B981" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid vertical={false} />
                      <XAxis dataKey="date" axisLine={false} tickLine={false} />
                      <YAxis axisLine={false} tickLine={false} width={40} />
                      <Tooltip
                        contentStyle={
                          isLight
                            ? { background: "#ffffff", border: "1px solid #C4CDD9", borderRadius: "8px", fontSize: "12px", boxShadow: "0 4px 16px rgba(0,0,0,0.1)" }
                            : { background: "#121212", border: "1px solid rgba(255,255,255,0.08)", borderRadius: "8px", fontSize: "12px" }
                        }
                        labelStyle={{ color: isLight ? "#444456" : "#a1a1aa" }}
                        itemStyle={{ color: "#10B981" }}
                      />
                      <Area type="monotone" dataKey="cumulative_profit" stroke="#10B981" strokeWidth={2} fill="url(#profitGrad)" />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <EmptyState
                    title="Todavía no hay historial suficiente"
                    desc="Los datos aparecerán a medida que se registren jugadas reales con resultado cerrado."
                  />
                )}
              </div>
            </div>

            <div className="space-y-6">
              <div className="rounded-2xl p-6 relative overflow-hidden"
                style={{ background: "linear-gradient(145deg, #1E1E26 0%, #16161A 100%)", border: "1px solid rgba(212,175,55,0.18)", boxShadow: "0 6px 28px rgba(0,0,0,0.5)" }}>
                <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 2, background: "linear-gradient(90deg, transparent, rgba(212,175,55,0.7), transparent)" }} />
                <div className="text-[10px] uppercase tracking-[0.25em] font-black mb-4" style={{ color: "#8E8E99" }}>Mi acceso</div>
                {user?.role === "admin" ? (
                  <div>
                    <div className="flex items-center gap-2">
                      <ShieldCheck weight="fill" className="w-5 h-5 text-yellow-400" />
                      <div className="font-black text-2xl uppercase text-white">Admin</div>
                    </div>
                    <div className="text-xs text-zinc-500 mt-1">Acceso completo al sistema</div>
                  </div>
                ) : (
                  <div>
                    <div className="flex items-center gap-2">
                      {user?.plan === "vip" && <Crown weight="fill" className="w-5 h-5" style={{ color: "var(--gold)" }} />}
                      <div className="font-black text-2xl uppercase text-white">{user?.plan}</div>
                    </div>
                    <div className="text-xs text-zinc-500 mt-1">
                      {user?.subscription_status === "active" ? "Suscripción activa" : "Sin suscripción activa"}
                    </div>
                    {user?.subscription_expires_at && (
                      <div className="text-xs text-zinc-600 mt-1">
                        Vence: {new Date(user.subscription_expires_at).toLocaleDateString("es-ES")}
                      </div>
                    )}
                    {user?.plan !== "vip" && (
                      <Link to="/plans" className="btn-primary w-full mt-5 text-sm" data-testid="dashboard-plan-cta">
                        <Sparkle weight="fill" className="w-3.5 h-3.5" /> Mejorar plan
                      </Link>
                    )}
                  </div>
                )}
              </div>

              <div className="rounded-2xl p-6 relative overflow-hidden"
                style={{ background: "linear-gradient(145deg, #1E1E26 0%, #16161A 100%)", border: "1px solid rgba(212,175,55,0.15)", boxShadow: "0 6px 28px rgba(0,0,0,0.5)" }}>
                <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 2, background: "linear-gradient(90deg, transparent, rgba(212,175,55,0.7), transparent)" }} />
                <div className="text-[10px] uppercase tracking-[0.25em] font-black mb-4" style={{ color: "#8E8E99" }}>Desglose</div>
                <div className="space-y-3">
                  {[
                    { k: "Ganadas", v: overall.won, color: "bg-emerald-400" },
                    { k: "Perdidas", v: overall.lost, color: "bg-red-400" },
                    { k: "Pendientes", v: overall.pending, color: "bg-yellow-400" },
                    { k: "Anuladas", v: overall.void, color: "bg-zinc-400" },
                  ].map((s) => (
                    <div key={s.k} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2.5">
                        <div className={`w-1.5 h-1.5 rounded-full ${s.color}`} />
                        <span className="text-zinc-400">{s.k}</span>
                      </div>
                      <span className="number-ticker font-bold">{s.v || 0}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/* ── Reloj en vivo por deporte (minuto / cuarto / entrada) ──── */
function liveClockLabel(ev) {
  const clock = ev.display_clock || "";
  const period = ev.period || 0;
  if (ev.sport === "football") {
    return clock ? `${clock}'` : period === 2 ? "2T" : "1T";
  }
  if (ev.sport === "nba") {
    const q = period ? `Q${period}` : "";
    return clock ? `${q} · ${clock}`.trim() : q || "EN JUEGO";
  }
  if (ev.sport === "mlb") {
    return period ? `${period}ª ent.` : "EN JUEGO";
  }
  return clock || "EN JUEGO";
}

function LiveEventCard({ event }) {
  const ev = event;
  return (
    <div
      className="rounded-2xl p-5 flex-shrink-0 w-[280px] lg:w-auto transition-all duration-300 hover:-translate-y-1"
      data-testid={`live-event-card-${ev.id}`}
      style={{
        background: "linear-gradient(145deg, #141a16 0%, #0f1411 100%)",
        border: "1px solid rgba(239,68,68,0.3)",
        boxShadow: "0 4px 24px rgba(239,68,68,0.08), 0 2px 8px rgba(0,0,0,0.4)",
      }}
    >
      <div className="flex items-center justify-between mb-4">
        <span
          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider text-white"
          style={{ background: "rgba(239,68,68,0.15)", border: "1px solid rgba(239,68,68,0.35)" }}
        >
          <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" /> EN VIVO
        </span>
        <span className="font-black text-sm" style={{ color: "var(--gold)" }}>{liveClockLabel(ev)}</span>
      </div>
      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <TeamLogo
            logoUrl={ev.home_logo || resolveTeamLogo(ev.sport, ev.home_team)}
            teamName={ev.home_team}
            size={32}
            rounded="md"
          />
          <span className="text-sm font-semibold truncate flex-1 text-zinc-100">{ev.home_team}</span>
          {ev.home_score != null && <span className="number-ticker font-black text-2xl text-white">{ev.home_score}</span>}
        </div>
        <div className="flex items-center gap-3">
          <TeamLogo
            logoUrl={ev.away_logo || resolveTeamLogo(ev.sport, ev.away_team)}
            teamName={ev.away_team}
            size={32}
            rounded="md"
          />
          <span className="text-sm font-semibold truncate flex-1 text-zinc-100">{ev.away_team}</span>
          {ev.away_score != null && <span className="number-ticker font-black text-2xl text-white">{ev.away_score}</span>}
        </div>
      </div>
      <div className="flex items-center gap-2 mt-4 pt-3 text-xs text-zinc-500 uppercase tracking-wider font-semibold"
        style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}>
        <SportIcon sport={ev.sport} size={14} />
        <span className="truncate">{ev.league}</span>
      </div>
    </div>
  );
}

function EmptyCard({ title, desc }) {
  return (
    <div className="rounded-2xl p-10 text-center" style={{ background: "#16161A", border: "1px solid rgba(255,255,255,0.07)", boxShadow: "0 4px 20px rgba(0,0,0,0.4)" }}>
      <div className="w-14 h-14 rounded-full mx-auto mb-4 flex items-center justify-center"
        style={{ background: "rgba(212,175,55,0.07)", border: "1px solid rgba(212,175,55,0.18)" }}>
        <Sparkle weight="regular" className="w-6 h-6 text-yellow-400" />
      </div>
      <div className="text-display font-bold text-lg text-white">{title}</div>
      <div className="text-sm text-zinc-500 mt-2 max-w-md mx-auto leading-relaxed">{desc}</div>
    </div>
  );
}

function EmptyState({ title, desc }) {
  return (
    <div className="h-full flex flex-col items-center justify-center text-center px-4">
      <div className="text-display font-bold text-base text-zinc-300">{title}</div>
      <div className="text-xs text-zinc-500 mt-1.5 max-w-xs">{desc}</div>
    </div>
  );
}
